import { Row, Section } from "anketa-core/group";
import { Translation } from "anketa-core/i18n";
import { OpenQuestion } from "anketa-core/open_question";
import {
  AnketaCheckPermissionOptions,
  AnketaResourcesEnum,
  calculateHostIP,
} from "../../../../";
import { QuestionsRightsTypes } from "..";
import { TextValidator } from "anketa-core/validation";
import { TextValidatorType } from "anketa-core/base";

const generateKubernetesServer = (
  offset: number,
  agentIdx: number,
  vpcName: string,
  exanioCidr: string
): Row => {
  const hostname = `${vpcName}-m0-k3server-00${agentIdx}`;
  const fqdn = `${hostname}.exanio.net`;
  const baseGroup = new Row(`server_${agentIdx}`, [
    new Translation("en", fqdn),
    new Translation("de", fqdn),
  ]);

  const firstCpeHostOffset = offset + 20;
  const cpeIp = exanioCidr
    ? calculateHostIP(exanioCidr, firstCpeHostOffset)
    : "";

  const ip = new OpenQuestion(`server_${agentIdx}_ip`, [
    new Translation("en", "CPE IP address"),
    new Translation("de", "CPE IP-Adresse"),
  ]);
  ip.setFactValue(cpeIp);
  ip.readonly = true;
  baseGroup.addChild(ip);

  return baseGroup;
};

export const generateKubernetesServers = (
  serverCount: number,
  vpcName: string,
  exanioCidr: string,
  questionsRights: (q: QuestionsRightsTypes) => void
): Section => {
  const servers = new Section("servers", [
    new Translation("en", "Update Servers"),
    new Translation("de", "Update Server"),
  ]);

  servers.description = [
    new Translation(
      "en",
      `
Your update servers are dedicated and intended exclusively for accessing your appliance. 

All necessary updates, APT packages and images are provided centrally from our servers and carefully checked. 

It is therefore not necessary to obtain packages from public repositories - maximum security and control for your environment.
`
    ),
    new Translation(
      "de",
      `
Ihre Update Server sind dediziert und ausschließlich für den Zugriff Ihrer Appliance vorgesehen. 

Alle notwendigen Updates, APT-Pakete und Images werden zentral von unseren Servern bereitgestellt und sorgfältig geprüft. 

Es ist somit nicht erforderlich, Pakete aus öffentlichen Repositories zu beziehen, das bedeutet maximale Sicherheit und Kontrolle für Ihre Umgebung.   
    `
    ),
  ];
  const serverSharedId = calculateHostIP(exanioCidr, 20);
  const sharedIp = new OpenQuestion(`server_shared_ip`, [
    new Translation("en", "Shared IP address"),
    new Translation("de", "Shared IP-Adresse"),
  ]);
  questionsRights(sharedIp);

  sharedIp.setFactValue(serverSharedId);
  sharedIp.readonly = true;
  servers.addChild(sharedIp);

  const numbers: Array<number> = [];
  if (typeof serverCount === "number" && vpcName && vpcName.trim() !== "") {
    for (let i = 1; i < serverCount + 1; i++) {
      numbers.push(i);
    }
    numbers.forEach((number, idx) => {
      servers.addChild(
        generateKubernetesServer(idx + 1, number, vpcName, exanioCidr)
      );
    });
  }

  return servers;
};

export const generateSwitch = (
  questionsRights: (
    q: QuestionsRightsTypes,
    options?: AnketaCheckPermissionOptions<AnketaResourcesEnum.questionnaires>
  ) => void
): Section => {
  const networkSwitch = new Section("switch", [
    new Translation("en", "Network Switch"),
    new Translation("de", "Netzwerk Switch  "),
  ]);

  networkSwitch.description = [
    new Translation(
      "en",
      `The network switch is supplied by us and provides the physical interface between our appliance and your environment.`
    ),
    new Translation(
      "de",
      `Der Netzwerkswitch wird von uns mitgeliefert und stellt die physische Schnittstelle zwischen unserer Appliance und Ihrer Umgebung her.`
    ),
  ];

  const networkSwitchRow = new Row("switch_row", [
    new Translation("en", "Network Switch"),
    new Translation("de", "Netzwerk Switch  "),
  ]);
  networkSwitch.addChild(networkSwitchRow);

  const model = new OpenQuestion("model", [
    new Translation("en", `Model`),
    new Translation("de", `Modell`),
  ]);
  questionsRights(model, {
    isInternal: true,
  });
  networkSwitchRow.addChild(model);
  model.validator = new TextValidator();

  const mac = new OpenQuestion("switch_mac", [
    new Translation("en", `MAC-Address`),
    new Translation("de", `MAC-Adresse`),
  ]);
  questionsRights(mac, {
    isInternal: true,
  });
  networkSwitchRow.addChild(mac);
  mac.validator = new TextValidator(TextValidatorType.MacAddress);

  const sn = new OpenQuestion("switch_sn", [
    new Translation("en", `Serial number`),
    new Translation("de", `Seriennummer`),
  ]);
  questionsRights(sn, {
    isInternal: true,
  });
  networkSwitchRow.addChild(sn);
  sn.validator = new TextValidator();

  return networkSwitch;
};
